"use client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

export type Membership = { organization_id: string; workspace_id: string | null; role: string };
export type Me = {
  id: string;
  email: string;
  name: string | null;
  language: string | null;
  timezone: string | null;
  is_super_admin: boolean;
  platform_role: string | null;
  role: string;
  memberships: Membership[];
};

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api<Me>("/auth/me");
      } catch {
        return null;
      }
    },
    staleTime: 30_000,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { language?: string; timezone?: string; name?: string }) =>
      api("/auth/me", { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

/** Which social providers the API actually has credentials for, the same
 * list the login page's buttons and the Settings > Authentication Methods
 * card both key off of, so a provider only ever reads as available in one
 * place if it's real everywhere. An unconfigured provider used to render its
 * "Continue with…" button on the login page anyway, 400ing on click. */
export function useOAuthProviders() {
  return useQuery<string[]>({
    queryKey: ["oauth-providers"],
    queryFn: async () => (await api<{ providers: string[] }>("/auth/oauth/providers")).providers,
    staleTime: 5 * 60_000,
  });
}

export function useRequestOtp() {
  return useMutation({
    mutationFn: (email: string) =>
      api("/auth/otp/request", { method: "POST", body: JSON.stringify({ email }) }),
  });
}

/** Either a real login (a session cookie is now set) or a 2FA-enrolled
 * account's first factor clearing, no cookie yet, `challenge` is what
 * useVerifyTotpLogin needs to finish the job. */
export type OtpVerifyResult = { needs_onboarding: boolean; needs_2fa?: false } | { needs_2fa: true; challenge: string };

export function useVerifyOtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; code: string }) =>
      api<OtpVerifyResult>("/auth/otp/verify", { method: "POST", body: JSON.stringify(v) }),
    onSuccess: (res) => { if (!res.needs_2fa) qc.invalidateQueries({ queryKey: ["me"] }); },
  });
}

/** Completes a login otp/verify or the OAuth callback deferred behind a 2FA
 * challenge. `challenge` is only present for the email-OTP path, where it
 * came back in the response body and lives in memory; the OAuth path's
 * challenge never reaches the browser as a value at all, it's an httpOnly
 * `mfa_ticket` cookie the backend reads on its own, so `challenge` is simply
 * omitted here and the request just needs to carry cookies (api() already
 * does). */
export function useVerifyTotpLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { challenge?: string; code: string }) =>
      api<{ needs_onboarding: boolean }>("/auth/2fa/login-verify", { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api("/organizations", { method: "POST", body: JSON.stringify({ name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

/** Full-page navigation to the sign-in screen.
 *
 * Deliberately not `router.push`: a soft navigation keeps the React Query cache
 * and the whole JS heap alive, so the signed-out user's agents, conversations
 * and billing figures stay in memory and reappear on Back. Replacing the
 * document drops all of it and leaves no history entry to go back to.
 */
function hardRedirectToLogin() {
  if (typeof window !== "undefined") window.location.replace("/login");
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/auth/logout", { method: "POST" }),
    // Redirect on settled, not onSuccess: if the logout request itself fails
    // (network drop, already-expired session) the cookies may still be gone, and
    // leaving the user sitting in a dead app shell is the bug being fixed here.
    onSettled: () => {
      qc.clear();
      hardRedirectToLogin();
    },
  });
}

export { hardRedirectToLogin };
